import { commitQuantityInput } from '@supply-mover-2-0/core';
import React from 'react';
import { Input } from './Input';

export interface QuantityInputProps
	extends Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		'value' | 'onChange' | 'type' | 'min' | 'max' | 'size'
	> {
	/** Current integer quantity. A value of 0 renders as an EMPTY field (see below). */
	value: number;
	/** Called with the parsed, clamped integer on every edit. */
	onChange: (value: number) => void;
	/** Lowest allowed value; also the value a blank field commits to. Defaults to 0. */
	min?: number;
	/** Highest allowed value, when the field is bounded. */
	max?: number;
	error?: boolean;
	fullWidth?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Controlled integer-quantity input.
 *
 * Fixes the "the 0 doesn't disappear" class of bug (PD-13758). A plain
 * number-backed input renders a literal "0" that snaps right back the instant
 * the field is cleared (the empty string is coerced to 0), so users end up
 * fighting a stubborn leading zero. Here we:
 *   - render a value of 0 as an EMPTY field (with a "0" placeholder) so it can
 *     actually be cleared and typed into,
 *   - select the field's contents on focus so the first keystroke replaces the
 *     current value instead of inserting alongside it,
 *   - commit a clamped integer to `onChange` via `commitQuantityInput`.
 *
 * This is the plain-field counterpart to `QuantitySelector` (the +/- stepper
 * pill); use this where the design calls for a normal bordered number field.
 */
export const QuantityInput = React.forwardRef<
	HTMLInputElement,
	QuantityInputProps
>(
	(
		{ value, onChange, min = 0, max, placeholder = '0', onFocus, ...props },
		ref,
	) => (
		<Input
			{...props}
			ref={ref}
			type="number"
			inputMode="numeric"
			min={min}
			max={max}
			placeholder={placeholder}
			// Render 0 as empty so the placeholder shows and the field can be
			// cleared without it snapping back to "0".
			value={value === 0 ? '' : value}
			onFocus={(e) => {
				// Select-all so typing replaces the value rather than fighting
				// the existing leading digit.
				e.currentTarget.select();
				onFocus?.(e);
			}}
			onChange={(e) => {
				onChange(commitQuantityInput(e.currentTarget.value, { min, max }));
			}}
		/>
	),
);

QuantityInput.displayName = 'QuantityInput';
